import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Sidebar } from "@/components/Sidebar";
import AIChat from "@/components/AIChat";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Megaphone, Plus, TrendingUp, Eye, MousePointer, Car, MessageSquare,
  Brain, Bot, ChevronRight, Sparkles, Star, AlertCircle, BarChart3, DollarSign
} from "lucide-react";

export default function MarketingPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [showAddCampaign, setShowAddCampaign] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: "", type: "social", target_audience: "", budget: 1000,
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [campaignsRes, reviewsRes, workersRes] = await Promise.all([
        axios.get(`${API}/campaigns`),
        axios.get(`${API}/reviews`),
        axios.get(`${API}/digital-workers/marketing`)
      ]);
      setCampaigns(campaignsRes.data);
      setReviews(reviewsRes.data);
      setWorkers(workersRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCampaign = async () => {
    try {
      await axios.post(`${API}/campaigns`, newCampaign);
      setShowAddCampaign(false);
      setNewCampaign({
        name: "", type: "social", target_audience: "", budget: 1000,
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0]
      });
      fetchData();
    } catch (error) {
      console.error("Error adding campaign:", error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: "bg-emerald-500/10 text-emerald-500",
      paused: "bg-amber-500/10 text-amber-500",
      draft: "bg-slate-500/10 text-slate-400",
      completed: "bg-blue-500/10 text-blue-500"
    };
    return colors[status] || colors.draft;
  };

  const getSentimentColor = (sentiment) => {
    const colors = {
      positive: "bg-emerald-500/10 text-emerald-500",
      negative: "bg-red-500/10 text-red-500",
      neutral: "bg-slate-500/10 text-slate-400"
    };
    return colors[sentiment] || colors.neutral;
  };

  const totalSpent = campaigns.reduce((a, c) => a + (c.spent || 0), 0);
  const totalImpressions = campaigns.reduce((a, c) => a + (c.impressions || 0), 0);
  const totalConversions = campaigns.reduce((a, c) => a + (c.conversions || 0), 0);
  const totalUnitsSold = campaigns.reduce((a, c) => a + (c.units_sold || 0), 0);

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-content">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold text-foreground flex items-center gap-3" data-testid="marketing-title">
              <Megaphone className="w-8 h-8 text-purple-500" />
              Digital Marketing
            </h1>
            <p className="text-muted-foreground">AI-driven campaigns and reputation management</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowChat(true)} className="gap-2">
              <Brain className="w-4 h-4" /> AI Assistant
            </Button>
            <Dialog open={showAddCampaign} onOpenChange={setShowAddCampaign}>
              <DialogTrigger asChild>
                <Button className="btn-ai gap-2" data-testid="add-campaign-btn">
                  <Plus className="w-4 h-4" /> Create Campaign
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Campaign</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <Input placeholder="Campaign Name" value={newCampaign.name} onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})} />
                  <Select value={newCampaign.type} onValueChange={(v) => setNewCampaign({...newCampaign, type: v})}>
                    <SelectTrigger><SelectValue placeholder="Campaign Type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="social">Social Media</SelectItem>
                      <SelectItem value="display">Display Ads</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="search">Search</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input placeholder="Target Audience" value={newCampaign.target_audience} onChange={(e) => setNewCampaign({...newCampaign, target_audience: e.target.value})} />
                  <Input type="number" placeholder="Budget ($)" value={newCampaign.budget} onChange={(e) => setNewCampaign({...newCampaign, budget: Number(e.target.value)})} />
                  <div className="grid grid-cols-2 gap-4">
                    <Input type="date" value={newCampaign.start_date} onChange={(e) => setNewCampaign({...newCampaign, start_date: e.target.value})} />
                    <Input type="date" value={newCampaign.end_date} onChange={(e) => setNewCampaign({...newCampaign, end_date: e.target.value})} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddCampaign(false)}>Cancel</Button>
                  <Button className="btn-ai" onClick={handleAddCampaign}>Create Campaign</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Tabs defaultValue="campaigns" className="space-y-6">
          <TabsList>
            <TabsTrigger value="campaigns" className="gap-2"><BarChart3 className="w-4 h-4" /> Campaigns</TabsTrigger>
            <TabsTrigger value="reviews" className="gap-2"><Star className="w-4 h-4" /> Reviews</TabsTrigger>
            <TabsTrigger value="workers" className="gap-2"><Bot className="w-4 h-4" /> Digital Workers</TabsTrigger>
          </TabsList>

          {/* Campaigns Tab */}
          <TabsContent value="campaigns" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Total Spent", value: `$${totalSpent.toLocaleString()}`, icon: DollarSign, color: "text-purple-500" },
                { label: "Impressions", value: totalImpressions.toLocaleString(), icon: Eye, color: "text-blue-500" },
                { label: "Conversions", value: totalConversions, icon: MousePointer, color: "text-emerald-500" },
                { label: "Units Sold", value: totalUnitsSold, icon: Car, color: "text-amber-500" },
              ].map((stat, idx) => (
                <Card key={idx}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg ${stat.color.replace('text-', 'bg-')}/10 flex items-center justify-center`}>
                      <stat.icon className={`w-5 h-5 ${stat.color}`} />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{stat.value}</div>
                      <div className="text-xs text-muted-foreground">{stat.label}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Campaigns List */}
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading campaigns...</div>
              ) : campaigns.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No campaigns found</div>
              ) : (
                campaigns.map((campaign) => (
                  <Card key={campaign.id} className="hover:border-purple-500/30 transition-all" data-testid={`campaign-${campaign.id}`}>
                    <CardContent className="p-5">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                            <Megaphone className="w-5 h-5 text-purple-500" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-foreground">{campaign.name}</span>
                              {campaign.ai_generated && (
                                <Badge className="bg-blue-500/10 text-blue-500 text-xs">
                                  <Sparkles className="w-3 h-3 mr-1" /> AI Generated
                                </Badge>
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground">{campaign.target_audience}</div>
                          </div>
                        </div>

                        <div className="flex items-center gap-6">
                          <div className="grid grid-cols-4 gap-4 text-center">
                            <div>
                              <div className="text-sm font-bold text-foreground">{campaign.impressions?.toLocaleString() || 0}</div>
                              <div className="text-xs text-muted-foreground">Impressions</div>
                            </div>
                            <div>
                              <div className="text-sm font-bold text-foreground">{campaign.clicks?.toLocaleString() || 0}</div>
                              <div className="text-xs text-muted-foreground">Clicks</div>
                            </div>
                            <div>
                              <div className="text-sm font-bold text-foreground">{campaign.conversions || 0}</div>
                              <div className="text-xs text-muted-foreground">Conversions</div>
                            </div>
                            <div>
                              <div className="text-sm font-bold text-emerald-500">{campaign.units_sold || 0}</div>
                              <div className="text-xs text-muted-foreground">Units Sold</div>
                            </div>
                          </div>

                          <div className="text-right">
                            <div className="text-lg font-bold text-foreground">${campaign.spent?.toLocaleString() || 0}</div>
                            <div className="text-xs text-muted-foreground">of ${campaign.budget?.toLocaleString()}</div>
                          </div>

                          <Badge className={getStatusColor(campaign.status)}>{campaign.status}</Badge>
                        </div>
                      </div>

                      {campaign.ai_optimization_notes && (
                        <div className="mt-4 p-3 rounded-lg bg-purple-500/5 border border-purple-500/20 flex items-start gap-2">
                          <Sparkles className="w-4 h-4 text-purple-500 mt-0.5" />
                          <span className="text-sm text-purple-400">{campaign.ai_optimization_notes}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews" className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[
                { label: "Total Reviews", value: reviews.length, icon: Star, color: "text-amber-500" },
                { label: "Avg Rating", value: reviews.length ? (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length).toFixed(1) : "0", icon: Star, color: "text-emerald-500" },
                { label: "Needs Response", value: reviews.filter(r => !r.response_sent).length, icon: MessageSquare, color: "text-blue-500" },
                { label: "Rescue Needed", value: reviews.filter(r => r.rescue_needed).length, icon: AlertCircle, color: "text-red-500" },
              ].map((stat, idx) => (
                <Card key={idx}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg ${stat.color.replace('text-', 'bg-')}/10 flex items-center justify-center`}>
                      <stat.icon className={`w-5 h-5 ${stat.color}`} />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{stat.value}</div>
                      <div className="text-xs text-muted-foreground">{stat.label}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="space-y-4">
              {reviews.map((review) => (
                <Card key={review.id} className={`hover:border-purple-500/30 transition-all ${review.rescue_needed ? 'border-red-500/30' : ''}`} data-testid={`review-${review.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'text-amber-500 fill-amber-500' : 'text-slate-600'}`} />
                          ))}
                        </div>
                        <div>
                          <div className="font-semibold text-foreground">{review.customer_name}</div>
                          <div className="text-xs text-muted-foreground">{review.platform}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getSentimentColor(review.sentiment)}>{review.sentiment}</Badge>
                        {review.rescue_needed && <Badge className="bg-red-500/10 text-red-500">Rescue Needed</Badge>}
                        {review.response_sent && <Badge className="bg-emerald-500/10 text-emerald-500">Responded</Badge>}
                      </div>
                    </div>
                    
                    <p className="mt-3 text-sm text-muted-foreground">{review.content}</p>

                    {review.ai_response_draft && !review.response_sent && (
                      <div className="mt-4 p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                        <div className="flex items-center gap-2 text-blue-500 font-medium mb-2">
                          <Sparkles className="w-4 h-4" /> AI Draft Response
                        </div>
                        <p className="text-sm text-blue-400">{review.ai_response_draft}</p>
                        <Button size="sm" className="mt-3 btn-ai">Approve & Send</Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Digital Workers Tab */}
          <TabsContent value="workers" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {workers.map((worker) => (
                <Card key={worker.id} className="hover:border-purple-500/30 transition-all group" data-testid={`worker-${worker.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Bot className="w-6 h-6 text-purple-500" />
                      </div>
                      <Badge className="bg-emerald-500/10 text-emerald-500">{worker.status}</Badge>
                    </div>
                    <CardTitle className="text-lg mt-3">{worker.name}</CardTitle>
                    <CardDescription className="text-purple-400">{worker.subtitle}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">{worker.description}</p>
                    
                    <div className="space-y-2">
                      {worker.features?.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                          <ChevronRight className="w-3 h-3 text-purple-500" />
                          {feature}
                        </div>
                      ))}
                    </div>

                    {worker.metrics && (
                      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-border">
                        {Object.entries(worker.metrics).map(([key, value]) => (
                          <div key={key} className="text-center">
                            <div className="text-sm font-bold text-foreground">{value}</div>
                            <div className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <AIChat isOpen={showChat} onClose={() => setShowChat(false)} vertical="marketing" />
      
      {!showChat && (
        <button onClick={() => setShowChat(true)} className="fixed bottom-6 right-6 w-14 h-14 rounded-full btn-ai flex items-center justify-center shadow-lg hover:scale-110 transition-transform" style={{ zIndex: 9998 }} data-testid="chat-fab">
          <MessageSquare className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
